"use client";

import Description from "@/components/frontend/ui/form/inputs/description";
import FieldError from "@/components/frontend/ui/form/inputs/field-error";
import Input from "@/components/frontend/ui/form/inputs/input";
import Label from "@/components/frontend/ui/form/inputs/label";
import { cn } from "@/lib/utils";
import { type FormFileBlock as FormFileBlockT } from "@/payload-types";
import { FileText, UploadCloud, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useId, useState } from "react";

type FormFileBlockProps = FormFileBlockT & {
	containerClassName?: string;
	fieldName?: string;
	fieldValue?: unknown;
	fieldError?: string;
	onFieldChange?: (value: unknown) => void;
	onFieldBlur?: () => void;
};

export const FormFileBlock: React.FC<FormFileBlockProps> = ({
	content,
	settings,
	layout,
	containerClassName,
	fieldName,
	fieldValue,
	fieldError,
	onFieldChange,
	onFieldBlur,
}) => {
	const t = useTranslations("forms.dropzone");
	const id = useId();
	const [isDragging, setIsDragging] = useState(false);

	if (!layout?.dropzone) {
		return (
			<Input
				name={fieldName}
				type="file"
				accept={settings?.accept ?? undefined}
				multiple={settings?.multiple ?? false}
				onChange={(event) => onFieldChange?.(Array.from(event.target.files ?? []))}
				onBlur={onFieldBlur}
				label={content.label}
				hideLabel={settings?.hideLabel ?? undefined}
				description={content.description}
				error={fieldError}
				required={content.required ?? false}
				containerClassName={containerClassName}
			/>
		);
	}

	const files = Array.isArray(fieldValue) ? (fieldValue as File[]) : [];

	const addFiles = (newFiles: File[]) => {
		if (newFiles.length === 0) return;
		onFieldChange?.(settings.multiple ? [...files, ...newFiles] : newFiles.slice(0, 1));
	};

	return (
		<div className={cn("flex flex-col items-start gap-2", containerClassName)}>
			<Label variant="native" htmlFor={id} required={content.required ?? false} visible={!settings.hideLabel}>
				{content.label}
			</Label>

			<label
				onDragOver={(event) => {
					event.preventDefault();
					setIsDragging(true);
				}}
				onDragLeave={() => setIsDragging(false)}
				onDrop={(event) => {
					event.preventDefault();
					setIsDragging(false);
					addFiles(Array.from(event.dataTransfer.files));
				}}
				data-dragging={isDragging || undefined}
				className="ms-input flex w-full cursor-pointer flex-col items-center gap-3 border-dashed text-center transition-colors has-focus-visible:outline-2 has-focus-visible:-outline-offset-1"
			>
				<input
					id={id}
					name={fieldName}
					type="file"
					className="sr-only"
					accept={settings.accept ?? undefined}
					multiple={settings.multiple ?? false}
					onChange={(event) => {
						addFiles(Array.from(event.target.files ?? []));
						event.target.value = "";
					}}
					onBlur={onFieldBlur}
				/>
				<UploadCloud className={cn("mt-4 size-8 opacity-70 transition-transform", isDragging && "scale-110")} />

				<span className="font-medium">{t.rich("title", { browse: (chunks) => <span className="underline">{chunks}</span> })}</span>

				<span className="mb-4 text-xs opacity-70">
					{settings.accept && `${settings.accept.replaceAll(",", ", ")} · `}
					{t("maxSize", { size: settings.maxFileSize })}
				</span>
			</label>

			{files.length > 0 && (
				<ul className="flex w-full flex-col gap-2">
					{files.map((file, index) => (
						<li key={`${file.name}-${index}`} className="ms-input flex items-center gap-3">
							<FileText className="size-5 shrink-0 opacity-60" />
							<span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
							<span className="shrink-0 text-xs opacity-60">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
							<button
								type="button"
								aria-label={t("remove", { name: file.name })}
								onClick={() => onFieldChange?.(files.filter((_, fileIndex) => fileIndex !== index))}
								className="shrink-0 cursor-pointer opacity-60 transition-opacity hover:opacity-100"
							>
								<X className="size-4" />
							</button>
						</li>
					))}
				</ul>
			)}

			{content.description && <Description>{content.description}</Description>}

			<FieldError message={fieldError} />
		</div>
	);
};
